import { t } from 'i18next';
import { useEffect, useState } from 'react';
import {
  CampaignsGetResponse_Campaign,
  CEventLinkFlag,
  CLandingpageColorKey,
  CLandingpageMediaKey
} from 'src/apis/api-client';
import './LandingPage.css';
import LpHero from './components/LpHero';
import LpStatementBand from './components/LpStatementBand';
import LpHowItWorks from './components/LpHowItWorks';
import LpRewards from './components/LpRewards';
import LpStageBand from './components/LpStageBand';
import LpProof from './components/LpProof';
import LpFaq from './components/LpFaq';
import LpFinalCta from './components/LpFinalCta';
import LpFooter from './components/LpFooter';

export type LandingPageProps = {
  campaignData: CampaignsGetResponse_Campaign;
};

export default function LandingPage({ campaignData }: LandingPageProps) {
  // Media
  const [heroUrl, setHeroUrl] = useState('/images/defaults/header.jpg');
  const [stageUrl, setStageUrl] = useState('/images/defaults/header.jpg');
  const [vibeUrl, setVibeUrl] = useState('/images/defaults/square.jpg');
  const [gimmickUrl, setGimmickUrl] = useState('/images/defaults/gimmick.png');

  // Brand colors (2 vars drive everything via CSS color-mix)
  const [brand, setBrand] = useState('e8431a');
  const [accent, setAccent] = useState('ffc24b');

  // Socials
  const [facebookLink, setFacebookLink] = useState<string | undefined>();
  const [instagramLink, setInstagramLink] = useState<string | undefined>();
  const [youtubeLink, setYouTubeLink] = useState<string | undefined>();

  // Blinking tab title (suppressed in iframe preview)
  useEffect(() => {
    if (window !== window.parent) return;
    let chg = false;
    const id = setInterval(() => {
      document.title = chg
        ? t('landingPage.slogan', { eventName: campaignData.event?.title })
        : t('landingPage.title', { eventName: campaignData.event?.title });
      chg = !chg;
    }, 1000);
    return () => clearInterval(id);
  }, [campaignData.event?.title]);

  // Derive media, colors, social links from campaign data
  useEffect(() => {
    if (!campaignData) return;

    campaignData.landingpage?.colors?.forEach(color => {
      if (!color.hexCode || !color.key) return;
      switch (color.key) {
        case CLandingpageColorKey.BRAND_COLOR:
          setBrand(color.hexCode);
          break;
        case CLandingpageColorKey.ACCENT_COLOR:
          setAccent(color.hexCode);
          break;
      }
    });

    campaignData.landingpage?.media?.forEach(media => {
      if (!media.key || !media.url) return;
      switch (media.key) {
        case CLandingpageMediaKey.HEADER:
          setHeroUrl(media.url);
          break;
        case CLandingpageMediaKey.BACKGROUND:
          setStageUrl(media.url);
          break;
        case CLandingpageMediaKey.SQUARE:
          setVibeUrl(media.url);
          break;
        case CLandingpageMediaKey.GIMMICK:
          setGimmickUrl(media.url);
          break;
      }
    });

    campaignData.event?.links?.forEach(link => {
      if (!link.flag || !link.value) return;
      switch (link.flag) {
        case CEventLinkFlag.FACEBOOK:   setFacebookLink(link.value);   break;
        case CEventLinkFlag.INSTAGRAM:  setInstagramLink(link.value);  break;
        case CEventLinkFlag.YOUTUBE:    setYouTubeLink(link.value);    break;
      }
    });
  }, [campaignData]);

  const eventName = campaignData.event?.title ?? '';
  const programId = campaignData.program?.id;
  const isPublished = campaignData.program?.isPublished;
  const eventCompany = campaignData.event?.company;

  return (
    <div
      className="lp-root"
      style={{
        '--brand': `#${brand}`,
        '--accent': `#${accent}`,
      } as React.CSSProperties}
    >
      <LpHero
        heroUrl={heroUrl}
        gimmickUrl={gimmickUrl}
        eventName={eventName}
        programId={programId}
        isPublished={isPublished}
      />
      <LpStatementBand eventName={eventName} />
      <LpHowItWorks />
      <LpRewards />
      <LpStageBand stageUrl={stageUrl} />
      <LpProof vibeUrl={vibeUrl} eventName={eventName} />
      <LpFaq />
      <LpFinalCta programId={programId} isPublished={isPublished} />
      <LpFooter
        facebookLink={facebookLink}
        instagramLink={instagramLink}
        youtubeLink={youtubeLink}
        eventCompany={eventCompany}
      />
    </div>
  );
}
